import {
  animate,
  ImageScaleView,
  ImageScaleViewOpts,
  Transition,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import { animDuration } from 'src/lib/utils';
import { GenericProgressBar } from './GenericProgressBar';

export class CircularProgressBar extends GenericProgressBar {
  /**
   * Set progress based on the domain.
   */
  public set progress(value: number) {
    this.updateProgress(value);
    this._progress = value;
  }

  private progressFillBg: [
    ImageScaleView,
    ImageScaleView,
    ImageScaleView,
    ImageScaleView,
  ];
  private progressFill: [
    ImageScaleView,
    ImageScaleView,
    ImageScaleView,
    ImageScaleView,
  ];
  public frame: ImageScaleView;

  constructor(
    opts: ViewOpts & {
      frameStyle?: Omit<ImageScaleViewOpts, 'superview'>;
      fillBgStyle?: Omit<ImageScaleViewOpts, 'superview'>;
      fillStyle?: Omit<ImageScaleViewOpts, 'superview'>;
    } & {
      domain?: number[];
      progress?: number;
    },
  ) {
    super({
      width: opts.frameStyle?.width ?? 100,
      height: opts.frameStyle?.width ?? 100,
      ...opts,
    });

    const fillBgStyle = {
      superview: this,
      image: 'assets/ui/hud/meters/level_fill_bg.png',
      x: this.style.width / 2,
      y: this.style.height / 2,
      width: this.style.width - 5,
      height: this.style.height - 5,
      centerOnOrigin: true,
      ...(opts.fillBgStyle || {}),
      zIndex: -4,
    };

    const fillStyle = {
      superview: this,
      image: 'assets/ui/hud/meters/level_fill.png',
      x: this.style.width / 2,
      y: this.style.height / 2,
      width: this.style.width - 5,
      height: this.style.height - 5,
      centerOnOrigin: true,
      ...(opts.fillStyle || {}),
      zIndex: -3,
    };

    // Create BG fill.
    this.progressFillBg = [
      new ImageScaleView({
        ...fillBgStyle,
      }),
      new ImageScaleView({
        ...fillBgStyle,
        r: Math.PI / 2,
      }),
      new ImageScaleView({
        ...fillBgStyle,
        r: Math.PI,
      }),
      new ImageScaleView({
        ...fillBgStyle,
        r: Math.PI + Math.PI / 2,
        zIndex: -2,
      }),
    ];

    // Create progress fill.
    this.progressFill = [
      new ImageScaleView({
        ...fillStyle,
      }),
      new ImageScaleView({
        ...fillStyle,
      }),
      new ImageScaleView({
        ...fillStyle,
      }),
      new ImageScaleView({
        ...fillStyle,
      }),
    ];

    // Create frame.
    this.frame = new ImageScaleView({
      image: 'assets/ui/hud/meters/level_bg.png',
      width: this.style.width,
      height: this.style.height,
      ...(opts.frameStyle || {}),
      superview: this,
      zIndex: 0,
      centerOnOrigin: false,
    });

    this._domain = opts.domain ?? [0, 1];
    this.progress = opts.progress ?? 0;
  }

  /**
   * Aniamte current progress to the given value.
   * @param value - Target value.
   * @param duration - Animation duration.
   */
  public async animateProgress(
    value: number,
    duration: number = animDuration,
    easing: Transition = animate.easeInOut,
  ) {
    if (this._progress === value) return;

    const initialValue = this._progress;
    this._progress = value;

    await animate(this)
      .then(
        (current) => {
          this.updateProgress(initialValue + current * (value - initialValue));
        },
        duration,
        easing,
      )
      .toPromise();
  }

  public updateFillBgOpts(
    opts: Partial<Omit<ImageScaleViewOpts, 'superview'>>,
  ) {
    this.progressFillBg.forEach((x) => x.updateOpts(opts));
  }

  public updateFillOpts(opts: Partial<Omit<ImageScaleViewOpts, 'superview'>>) {
    this.progressFill.forEach((x) => x.updateOpts(opts));
  }

  public setFrameOpts(opts: Partial<Omit<ImageScaleViewOpts, 'superview'>>) {
    this.frame.updateOpts(opts);
  }
  private updateProgress(value: number) {
    const normalizedProgress = this.getNormalizedProgress(value);
    const currentAngle = normalizedProgress * Math.PI * 2;

    for (let i = 0; i < this.progressFill.length; i++) {
      const minAngle = (i * Math.PI) / 2 - Math.PI / 2;
      const maxAngle = ((i + 1) * Math.PI) / 2 - Math.PI / 2;
      const targetRotation = Math.min(
        minAngle + currentAngle - maxAngle,
        maxAngle,
      );

      this.progressFill[i].updateOpts({
        r: targetRotation,
        zIndex:
          i === this.progressFill.length - 1 && targetRotation > 0 ? -1 : -3,
      });
    }
  }
}
